/**
 * 「从商品库带入」在两处以上的**共用派生** —— 现在是六个生成页。
 *
 * 用户口径（2026-10-09）：「其他页面也可以引用这个商品库啊」。
 * 于是同一段逻辑（商品图在前 + 已有的图接在后面 + 去重 + 按上限截断 + 如实报截断）
 * 会在详情页 / 社媒图 / 广告图 / 服饰图 / 视频页各出现一次。**各写一份的话，
 * 迟早只有五处被改** —— 而这类漂移的症状是「两页带入的图顺序不一样」，
 * 用户只会以为是自己拖错了。
 *
 * ⚠️ 上限（`max`）由调用方给：每个页面的参考图上限不一样（详情页 6、
 * 社媒图 6、服饰图按档位来）。这个函数**只做合并，不假定任何一个数**。
 */

/**
 * 把商品图组合并进当前参考图组。
 *
 * 三条口径（与详情页 2026-10-08 那版逐字一致，现在提取成唯一实现）：
 *
 * 1. **商品图在前，已有图接在后面** —— 商品的第一张是主图位（详情页第一张
 *    当封面），排在后面就只是「多了一张参考图」。
 * 2. **追加而不是替换** —— 替换会把用户自己已经传好的图悄悄删掉，
 *    而那个损失在界面上一闪就过去了。
 * 3. **`dropped` 如实返回**，由调用方说给用户听。**不许静默截断** ——
 *    「我带了 12 张，结果只进去 6 张」而界面一声不吭，是本项目反复出过的那类
 *    「看着成功、实际少了一半」。
 */
export function mergeProductRefs(
  current: string[],
  incoming: string[],
  max: number,
): { urls: string[]; dropped: number } {
  const add = incoming.filter(Boolean)
  const kept = current.filter((u) => u && !add.includes(u))
  const urls = [...add, ...kept].slice(0, max)
  return { urls, dropped: add.length + kept.length - urls.length }
}

/**
 * 带入后的提示语 —— **一处生成**，六个页面共用。
 *
 * 分两种说法：
 *   · 有图 → 「已带入「X」的标题与卖点，外加 N 张产品图」
 *   · 没图（文案工作台这类没有图片槽位的页 / 商品本身没图）→ 只说标题与卖点
 * 截断时补一句（上限 + 被丢掉的张数）。
 *
 * `what` 让调用方能改「标题与卖点」这个说法（服饰图页接的是「需求描述」，
 * 说「标题」会让人以为自己漏了个字段）。
 */
export function productImportMessage(
  title: string,
  {
    refs = 0,
    dropped = 0,
    max,
    what = '标题与卖点',
  }: { refs?: number; dropped?: number; max?: number; what?: string } = {},
): string {
  const head = `已带入「${title}」的${what}`
  if (!refs) return head
  const tail = dropped > 0 && max ? `（参考图上限 ${max} 张，另有 ${dropped} 张没带进来）` : ''
  return `${head}，外加 ${refs} 张产品图${tail}`
}
